// src/views/parent/PAAccueil.jsx
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useParentService } from "../../services/parentService";
import { urlimage } from "../../utils/config";
import { useParentDrawer } from "../../context/ParentDrawerContext";

const BADGE_POLL_MS = 30000;

// ─── HELPERS ──────────────────────────────────────────────────────────────
function getGreeting(username) {
  const h = new Date().getHours();
  if (h >= 5 && h < 12) return { text: `Bonjour ${username},\npassez une excellente journée !`, icon: "☀️" };
  if (h >= 12 && h < 18) return { text: `Bon après-midi ${username},\nrestez connecté avec l'école`, icon: "⛅" };
  if (h >= 18 && h < 22) return { text: `Bonsoir ${username},\nbonne fin de journée !`, icon: "🌙" };
  return { text: `Bonne nuit ${username},\nà demain pour le suivi !`, icon: "😴" };
}

function formatDateFR(dateStr) {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("fr-FR", {
    day: "2-digit", month: "2-digit", year: "numeric",
  });
}

// ─── SKELETON LOADER ──────────────────────────────────────────────────────
function SkeletonLoader() {
  return (
    <div style={{ background: "var(--s2)", minHeight: "100dvh" }}>
      <div style={{
        height: 270, borderRadius: "0 0 50px 50px",
        background: "linear-gradient(135deg, var(--pr-bg), var(--pr-lt))",
        animation: "skPulse 1.2s ease-in-out infinite alternate",
      }} />
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
        gap: 16,
        padding: 16,
        marginTop: 16,
      }}>
        {[1, 2, 3, 4].map(i => (
          <div key={i} style={{
            height: 340, borderRadius: 20, background: "var(--b2)",
            animation: "skPulse 1.2s ease-in-out infinite alternate",
            animationDelay: `${i * 0.15}s`,
          }} />
        ))}
      </div>
    </div>
  );
}

// ─── CARTE ENFANT ──────────────────────────────────────────────────────
function EnfantCard({ eleve, navigate, slug }) {
  const [hovered, setHovered] = useState(false);
  const [imgErr, setImgErr] = useState(false);
  const classeNom = eleve.inscriptions?.[0]?.classe_nom ?? "-";
  const photoSrc = eleve.photo ? `${urlimage}${eleve.photo}` : null;

  const fullName = `${eleve.prenom} ${eleve.nom}`;

  const handleClick = () => {
    navigate(`/${slug}/parent/entrees-sorties/${eleve.id}`);
  };

  return (
    <div
      onClick={handleClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: "#fff",
        borderRadius: 20,
        padding: "20px 16px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        boxShadow: hovered ? "0 12px 36px rgba(27,79,128,0.18)" : "0 4px 18px rgba(0,0,0,0.10)",
        cursor: "pointer",
        transform: hovered ? "translateY(-3px)" : "none",
        transition: "all 0.22s ease",
        height: "100%",
        minHeight: 330,
      }}
    >
      <div style={{
        width: 100, height: 100, borderRadius: "50%",
        border: "4px solid #fff",
        boxShadow: "0 5px 16px rgba(0,0,0,0.18)",
        overflow: "hidden", marginBottom: 14,
        background: "var(--pr-bg)",
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0,
      }}>
        {photoSrc && !imgErr ? (
          <img
            src={photoSrc}
            alt={fullName}
            onError={() => setImgErr(true)}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <i className="ti ti-user-circle" style={{ fontSize: 60, color: "var(--pr-lt)" }} />
        )}
      </div>

      <div style={{
        fontFamily: "Poppins, sans-serif",
        fontSize: 16, fontWeight: 700, textAlign: "center",
        marginBottom: 10, color: "var(--t)",
      }}>
        {fullName}
      </div>

      <div style={{ width: "100%", marginBottom: 16 }}>
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "center",
          gap: 8, fontFamily: "Inter, sans-serif", fontSize: 13,
          color: "var(--m)", marginBottom: 6,
        }}>
          <i className="ti ti-school" style={{ fontSize: 14, color: "var(--pr-lt)" }} />
          <span>{classeNom}</span>
        </div>

        <div style={{
          display: "flex", alignItems: "center", justifyContent: "center",
          gap: 8, fontFamily: "Inter, sans-serif", fontSize: 13,
          color: "var(--m)", marginBottom: 6,
        }}>
          <i className="ti ti-cake" style={{ fontSize: 14, color: "#f97316" }} />
          <span>{formatDateFR(eleve.date_naissance)}</span>
        </div>

        <div style={{
          display: "flex", alignItems: "center", justifyContent: "center",
          gap: 8, fontFamily: "Inter, sans-serif", fontSize: 13,
          color: "var(--m)",
        }}>
          <i className="ti ti-user" style={{ fontSize: 14, color: "#14b8a6" }} />
          <span>{eleve.genre === "M" ? "Masculin" : eleve.genre === "F" ? "Féminin" : eleve.genre ?? "-"}</span>
        </div>
      </div>

      <button
        onClick={(e) => { e.stopPropagation(); handleClick(); }}
        style={{
          marginTop: "auto",
          border: "1.5px solid var(--pr)",
          background: "none", color: "var(--pr)", borderRadius: 50,
          padding: "8px 18px", cursor: "pointer",
          fontFamily: "Inter, sans-serif", fontSize: 13,
          fontWeight: 500, transition: "all 0.18s",
          display: "flex", alignItems: "center", gap: 6,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "var(--pr)";
          e.currentTarget.style.color = "#fff";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "none";
          e.currentTarget.style.color = "var(--pr)";
        }}
      >
        <i className="ti ti-calendar-check" style={{ fontSize: 14 }} />
        Voir Entrées / Sorties
      </button>
    </div>
  );
}

// ─── HERO SECTION ──────────────────────────────────────────────────────────
function HeroSection({ username, badges, navigate, slug }) {
  const { openDrawer } = useParentDrawer();
  const { text, icon } = getGreeting(username);
  const total = badges.notifs + badges.messages;
  const lines = text.split("\n");

  return (
    <div style={{
      height: 280, flexShrink: 0,
      background: "linear-gradient(135deg, var(--pr-dk) 0%, var(--pr) 100%)",
      borderRadius: "0 0 50px 50px",
      boxShadow: "0 6px 24px rgba(0,0,0,0.22)",
      position: "relative", overflow: "hidden",
    }}>
      <div style={{
        position: "absolute", left: "20%", right: "20%", bottom: 0,
        height: 3,
        background: "linear-gradient(90deg, transparent, var(--or), transparent)",
      }} />

      <button
        onClick={openDrawer}
        style={{
          position: "absolute", top: 20, left: 16,
          background: "rgba(255,255,255,0.2)",
          border: "none", borderRadius: 12,
          width: 42, height: 42, cursor: "pointer", zIndex: 10,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}
      >
        <i className="ti ti-menu-2" style={{ color: "#fff", fontSize: 22 }} />
      </button>

      <div style={{ position: "absolute", top: 20, right: 16, display: "flex", gap: 8, zIndex: 10 }}>
        <button
          onClick={() => navigate(`/${slug}/parent/notifications`)}
          style={{
            background: "rgba(255,255,255,0.2)", border: "none",
            borderRadius: "50%", width: 42, height: 42,
            cursor: "pointer", position: "relative",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <i className="ti ti-bell" style={{ color: "#fff", fontSize: 20 }} />
          {total > 0 && (
            <div style={{
              position: "absolute", top: -4, right: -4,
              background: "var(--rd)", borderRadius: "50%",
              minWidth: 18, height: 18, fontSize: 10, fontWeight: 700,
              color: "#fff", display: "flex", alignItems: "center",
              justifyContent: "center", padding: "0 2px",
            }}>{total > 99 ? "99+" : total}</div>
          )}
        </button>
        <button
          onClick={() => navigate(`/${slug}/parent/profil`)}
          style={{
            background: "rgba(255,255,255,0.2)", border: "none",
            borderRadius: "50%", width: 42, height: 42, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <i className="ti ti-user-circle" style={{ color: "#fff", fontSize: 22 }} />
        </button>
      </div>

      <div style={{
        position: "absolute", bottom: 28, left: 24, right: 24,
        display: "flex", flexDirection: "column", alignItems: "center", gap: 14,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 28 }}>{icon}</span>
          <div style={{ textAlign: "center" }}>
            {lines.map((l, i) => (
              <div key={i} style={{
                fontFamily: "Poppins, sans-serif", color: "#fff",
                fontSize: i === 0 ? 18 : 14,
                fontWeight: i === 0 ? 700 : 400,
                textShadow: "0 2px 6px rgba(0,0,0,0.3)",
              }}>{l}</div>
            ))}
          </div>
        </div>
        <button
          onClick={() => navigate(`/${slug}/parent/abonnements`)}
          style={{
            background: "#fff", color: "var(--pr)", border: "none",
            borderRadius: 50, padding: "10px 24px", cursor: "pointer",
            fontWeight: 700, fontSize: 14,
            boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
            display: "flex", alignItems: "center", gap: 8,
          }}
        >
          <i className="ti ti-credit-card" style={{ color: "var(--or-dk)" }} />
          Mes abonnements
        </button>
      </div>
    </div>
  );
}

// ─── PAGE PRINCIPALE ──────────────────────────────────────────────────────
export default function PAAccueil({ slug }) {
  const navigate = useNavigate();
  const {
    getEnfants,
    getMonCompte,
    getMessagesNonLus,
    getNotificationsNonLues,
  } = useParentService();

  const [enfants, setEnfants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [username, setUsername] = useState("Parent");
  const [badges, setBadges] = useState({ notifs: 0, messages: 0 });

  // 🛡️ Réf pour éviter double-fetch (React 18 StrictMode) et boucle
  const hasFetchedRef = useRef(false);

  // ─── CHARGEMENT DES DONNÉES ─────────────────────────────────────────
  // Dépendance UNIQUEMENT sur slug → identité stable entre renders
  const loadData = useCallback(async () => {
    if (!slug) {
      setError("Identifiant parent manquant.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const [enfantsRes, userRes] = await Promise.all([
        getEnfants(slug),
        getMonCompte(slug),
      ]);
      setEnfants(enfantsRes || []);
      setUsername(userRes?.username || "Parent");
    } catch (err) {
      setError("Impossible de récupérer les données.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    // Empêche le double appel en StrictMode (dev) et toute boucle
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    loadData();
  }, [loadData]);

  // ─── CHARGEMENT DES BADGES ──────────────────────────────────────────
  // Dépendance UNIQUEMENT sur slug → intervalle stable
  const fetchBadges = useCallback(async () => {
    if (!slug) return;

    try {
      const [messagesRes, notifsRes] = await Promise.all([
        getMessagesNonLus(slug).catch(() => ({ count: 0 })),
        getNotificationsNonLues(slug).catch(() => ({ count: 0 })),
      ]);

      setBadges({
        messages: messagesRes?.count ?? 0,
        notifs: notifsRes?.count ?? 0,
      });
    } catch (error) {
      setBadges({ messages: 0, notifs: 0 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    fetchBadges();
    const interval = setInterval(fetchBadges, BADGE_POLL_MS);
    return () => clearInterval(interval);
  }, [fetchBadges]);

  // ─── RENDU ───────────────────────────────────────────────────────────
  // Skeleton uniquement pendant le chargement initial
  if (loading) return <SkeletonLoader />;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;700&family=Inter:wght@400;500;600;700&display=swap');

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-thumb { background: var(--b2); border-radius: 4px; }

        @keyframes skPulse {
          from { opacity: 0.55; }
          to { opacity: 1; }
        }

        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .pa-accueil-error {
          background: #fff;
          border-radius: 20px;
          padding: 40px 20px;
          text-align: center;
          box-shadow: 0 4px 18px rgba(0,0,0,0.08);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
        }

        .pa-accueil-error i.alert-icon {
          font-size: 48px;
          color: var(--rd);
        }

        .pa-accueil-error h2 {
          font-family: 'Poppins', sans-serif;
          font-size: 17px;
          font-weight: 600;
          color: var(--t);
          margin: 4px 0 0;
        }

        .pa-accueil-error p {
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          color: var(--m);
          margin: 0 0 12px;
        }

        .pa-accueil-error .actions-row {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          justify-content: center;
        }

        .pa-accueil-error .btn {
          border: none;
          border-radius: 30px;
          padding: 10px 22px;
          cursor: pointer;
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          font-weight: 600;
          color: #fff;
          background: var(--pr);
          display: inline-flex;
          align-items: center;
          gap: 6px;
          transition: opacity 0.2s;
        }

        .pa-accueil-error .btn:hover { opacity: 0.9; }

        .pa-accueil-error .btn.btn-secondary {
          background: var(--b2);
          color: var(--t);
        }
      `}</style>

      <div style={{
        background: "var(--s2)",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
      }}>
        <HeroSection
          username={username}
          badges={badges}
          navigate={navigate}
          slug={slug}
        />

        <div style={{ padding: 16, flex: 1 }}>
          {error ? (
            /* ── ÉTAT ERREUR (header conservé au-dessus) ── */
            <div className="pa-accueil-error">
              <i className="ti ti-alert-circle alert-icon" />
              <h2>Erreur de chargement</h2>
              <p>{error}</p>
              <div className="actions-row">
                <button className="btn" onClick={loadData}>
                  <i className="ti ti-refresh" />
                  Réessayer
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => navigate(`/${slug}/parent/profil`)}
                >
                  <i className="ti ti-user-circle" />
                  Mon profil
                </button>
              </div>
            </div>
          ) : enfants.length === 0 ? (
            /* ── ÉTAT VIDE ── */
            <div className="card" style={{
              textAlign: "center",
              padding: 40,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 12,
            }}>
              <span style={{ fontSize: 44 }}>👧</span>
              <p style={{ color: "var(--m)", fontSize: 16 }}>Aucun enfant associé pour le moment.</p>
              <p style={{ fontSize: 13, color: "var(--h)" }}>
                Contactez votre établissement pour lier votre compte.
              </p>
            </div>
          ) : (
            /* ── LISTE DES ENFANTS ── */
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: 16,
            }}>
              {enfants.map((eleve, i) => (
                <div
                  key={eleve.id}
                  style={{
                    animation: "fadeUp 0.4s ease both",
                    animationDelay: `${i * 0.1}s`,
                  }}
                >
                  <EnfantCard eleve={eleve} navigate={navigate} slug={slug} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}